import apiService from "../../../service/Api";
import {
  getRateAmount,
  IFormProps,
  IRateCardProps,
} from "../types/rate-card";

export interface AssignmentRoleInfo {
  id: number;
  name: string;
  description?: string | null;
}

export interface AssignmentRateResolution {
  rateCard: IRateCardProps;
  amount: number;
  currency: string;
}

export interface AssignmentStreamInfo {
  id: number;
  title: string;
  date: string;
  start: string;
  end: string;
}

const getDefaultEffectiveDate = () => new Date().toISOString().split("T")[0];

const getBaseRateValue = (currency: string) => ({
  amount: 0,
  currency,
  overtime_multiplier: 0,
  night_premium: 0,
  notes: "",
});

export const createEmptyAssignmentRateForm = (
  effectiveDate?: string,
): IFormProps => ({
  role: {
    name: "",
    description: "",
  },
  roleId: null,
  hourly_rate: 0,
  stream_rate: 0,
  daily_rate: 0,
  salaried_rate: 0,
  fixed_rate: 0,
  currency: "USD",
  effective_date: effectiveDate ?? getDefaultEffectiveDate(),
  last_updated_by: "",
});

export const createAssignmentRateForm = (
  role: AssignmentRoleInfo,
  effectiveDate?: string,
): IFormProps => ({
  ...createEmptyAssignmentRateForm(effectiveDate),
  role: {
    name: role.name,
    description: role.description ?? role.name,
  },
  roleId: role.id,
});

export const getAssignmentRoleLabel = (role: AssignmentRoleInfo) =>
  `${role.id} - ${role.name}`;

export const buildFixedRatePayload = (formData: IFormProps) => {
  const amount = Number(formData.fixed_rate ?? 0);
  const baseRateValue = getBaseRateValue(formData.currency);

  return {
    role: {
      name: formData.role.name,
      description: formData.role.description,
    },
    role_id: formData.roleId,
    rate: {
      hourly: { ...baseRateValue },
      stream: { ...baseRateValue },
      daily: { ...baseRateValue },
      salaried: { ...baseRateValue },
      fixed: {
        ...baseRateValue,
        amount,
      },
    },
    currency: formData.currency,
    effective_date: formData.effective_date,
    rating_type: "fixed" as const,
  };
};

export const normalizeAssignmentRate = (
  rateCard: IRateCardProps,
): AssignmentRateResolution => ({
  rateCard,
  amount: getRateAmount(rateCard.rate, "fixed"),
  currency: rateCard.rate?.fixed?.currency ?? rateCard.currency ?? "USD",
});

export const resolveRoleIdByName = (
  roleName: string | undefined,
  roles: Array<{ id: number; name: string }>,
): number | null => {
  if (!roleName?.trim()) return null;
  const normalized = roleName.trim().toLowerCase();
  const match = roles.find((r) => r.name.trim().toLowerCase() === normalized);
  return match?.id ?? null;
};

export const formatAssignmentPayout = (
  amount: number,
  currency: string,
): string => {
  const formatted = amount.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  if (currency === "ZWG") {
    return `ZWG ${formatted}`;
  }
  return `$${formatted}`;
};

export type AssignmentPayout = {
  amount: number;
  currency: string;
};

export type AssignmentPayoutMap = Record<number, AssignmentPayout | null>;

export const buildAssignmentPayoutMap = async (
  assignments: Array<{ id: number; assignment: Array<{ role: string }> }>,
  rolesDetail: Array<{ id: number; name: string }>,
): Promise<AssignmentPayoutMap> => {
  const payouts: AssignmentPayoutMap = {};
  const roleIdByName = new Map<string, number | null>();
  const rateByRoleId = new Map<number, AssignmentRateResolution | null>();

  for (const item of assignments) {
    const roleName = item.assignment[0]?.role ?? "";
    let roleId = roleIdByName.get(roleName);
    if (roleId === undefined) {
      roleId = resolveRoleIdByName(roleName, rolesDetail);
      roleIdByName.set(roleName, roleId);
    }

    if (!roleId) {
      payouts[item.id] = null;
      continue;
    }

    let rate = rateByRoleId.get(roleId);
    if (rate === undefined) {
      rate = await fetchFixedRoleRate(roleId);
      rateByRoleId.set(roleId, rate);
    }

    payouts[item.id] = rate
      ? { amount: rate.amount, currency: rate.currency }
      : null;
  }

  return payouts;
};

export const fetchFixedRoleRate = async (
  roleId: number,
): Promise<AssignmentRateResolution | null> => {
  const response = await apiService.get<{
    results: IRateCardProps[];
  }>("/api/crews/role-ratings/", {
    role: roleId,
    rating_type: "fixed",
    page_size: 1,
  });

  const rateCard = response.data.results?.[0];

  return rateCard ? normalizeAssignmentRate(rateCard) : null;
};

interface CreatePixelPilotJobAndGigParams {
  amount: number;
  assignedTo: number | null | undefined;
  callsheetId: number;
  crewMemberId: number;
  currency: string;
  description?: string | null;
  stream: AssignmentStreamInfo;
}

export const createPixelPilotJobAndGig = async ({
  amount,
  assignedTo,
  callsheetId,
  crewMemberId,
  currency,
  description,
  stream,
}: CreatePixelPilotJobAndGigParams) => {
  const jobResponse = await apiService.post<{ id: number }>(
    "/api/jobs/pixel-pilot-jobs/",
    {
      name: stream.title,
      currency,
      stream: stream.id,
      callsheet: callsheetId,
      assigned_to: assignedTo,
      amount,
      description: description ?? stream.title,
      status: "pending",
      start_time: `${stream.date}T${stream.start}`,
      end_time: `${stream.date}T${stream.end}`,
    },
  );

  await apiService.post("/api/gigs/pixel-pilot-gigs/", {
    job_id: jobResponse.data.id,
    crew_member_id: crewMemberId,
    status: "pending",
  });

  return jobResponse.data;
};
